import { Permiso } from '../auth/permisos';

/**
 * Iconos disponibles para el menú; los trazos están en `NavIconComponent`. Dos
 * filas que se dibujan a la vez —las secciones del menú expandido, las opciones
 * del rail colapsado— nunca comparten icono: en el rail el icono es lo único que
 * identifica a la opción.
 */
export type NavIcon =
  | 'home'
  | 'collection'
  | 'list'
  | 'clipboard'
  | 'copiar'
  | 'tubos'
  | 'check'
  | 'dna'
  | 'drop'
  | 'leche'
  | 'celulas'
  | 'virus'
  | 'caballo'
  | 'bovino'
  | 'vacuna'
  | 'pulmon'
  | 'ave'
  | 'placa'
  | 'petri'
  | 'cerdo'
  | 'grafico'
  | 'termociclador'
  | 'molecula'
  | 'bacteria'
  | 'ambiente'
  | 'agua'
  | 'cultivo'
  | 'huevo'
  | 'hongo'
  | 'microscopio'
  | 'salmonella'
  | 'recuento'
  | 'espiroqueta'
  | 'microaglutinacion'
  | 'cultivo-lepto'
  | 'rabia'
  | 'microscopio-fluor'
  | 'raton'
  | 'cerebro'
  | 'garrapata'
  | 'espermatozoide'
  | 'abeja'
  | 'larva'
  | 'escarabajo'
  | 'lombriz'
  | 'report'
  | 'receipt'
  | 'etiqueta'
  | 'plantilla'
  | 'usuarios';

export interface NavItem {
  label: string;
  route: string;
  icon: NavIcon;
  /** Permiso que habilita la opción; sin él la opción es pública. */
  permiso?: Permiso;
}

export interface NavSection {
  label: string;
  /**
   * Icono de la sección. En el menú expandido la sección es la fila que se
   * pliega, así que es ella la que lleva icono; sus opciones van indentadas y
   * sin él.
   */
  icon: NavIcon;
  /**
   * Con `true` la sección se dibuja como grupo plegable aunque hoy tenga una
   * sola opción. Es para las secciones que están empezando: sin esto el menú
   * las aplana a un enlace suelto y la pantalla queda colgando fuera de su
   * sección, hasta que llega la segunda hoja y el grupo aparece de golpe.
   */
  siempreGrupo?: boolean;
  items: NavItem[];
}

/**
 * Definición única del menú: de aquí salen el sidebar, y la ruta de inicio
 * según los permisos del usuario (`primeraRutaPermitida`). Los permisos de
 * cada RUTA se declaran en el `data.permiso` de su `*.routes.ts`.
 */
export const SECCIONES: readonly NavSection[] = [
  {
    label: 'General',
    icon: 'home',
    // Sin permiso: el tablero de inicio solo exige sesión, y por ir de primero
    // es también el destino al que `primeraRutaPermitida` manda a quien entra
    // a una pantalla que no le corresponde.
    items: [{ label: 'Inicio', route: '/inicio', icon: 'home' }],
  },
  {
    label: 'Recepción',
    icon: 'clipboard',
    items: [
      { label: 'Recepción de muestras', route: '/recepciones', icon: 'clipboard', permiso: Permiso.RECEPCIONES_VER },
      { label: 'Copia de IHIMV-RT-01', route: '/copia-recepcion', icon: 'copiar', permiso: Permiso.RECEPCIONES_COPIAR },
      { label: 'Separación de muestras', route: '/separacion-muestras', icon: 'tubos', permiso: Permiso.SEPARACION_VER },
      // Oculta por ahora: no se sabe si se va a usar. La ruta y la página siguen existiendo.
      // { label: 'Confirmación de muestras', route: '/confirmacion-muestras', icon: 'check', permiso: Permiso.CONFIRMACION_VER },
    ],
  },
  {
    label: 'Serología (SER)',
    icon: 'drop',
    items: [
      { label: 'Diagnóstico de brucelosis', route: '/serologia/brucelosis', icon: 'drop', permiso: Permiso.BRUCELOSIS_VER },
      { label: 'Brucelosis en leche', route: '/serologia/brucelosis-leche', icon: 'leche', permiso: Permiso.BRUCELOSIS_LECHE_VER },
      { label: 'Tuberculosis bovina (ELISA)', route: '/serologia/tuberculosis', icon: 'bovino', permiso: Permiso.TUBERCULOSIS_VER },
    ],
  },
  {
    label: 'Hematología (HEMA)',
    icon: 'celulas',
    items: [
      { label: 'Hemograma y Hemoparásitos', route: '/hematologia/hemograma', icon: 'celulas', permiso: Permiso.HEMOGRAMA_VER },
    ],
  },
  {
    label: 'Virología (VIR)',
    icon: 'virus',
    items: [
      { label: 'Anemia Infecciosa Equina', route: '/virologia/anemia-equina', icon: 'caballo', permiso: Permiso.ANEMIA_EQUINA_VER },
      { label: 'Laringotraqueítis Aviar (ELISA)', route: '/virologia/laringotraqueitis', icon: 'placa', permiso: Permiso.LARINGOTRAQUEITIS_VER },
      { label: 'Enfermedad de Newcastle (HI)', route: '/virologia/newcastle', icon: 'ave', permiso: Permiso.NEWCASTLE_VER },
      { label: 'Influenza Aviar (IDGA)', route: '/virologia/influenza-aviar', icon: 'petri', permiso: Permiso.INFLUENZA_AVIAR_VER },
      { label: 'Peste Porcina Clásica', route: '/virologia/peste-porcina-clasica', icon: 'cerdo', permiso: Permiso.PESTE_PORCINA_CLASICA_VER },
      { label: 'Peste Porcina Africana', route: '/virologia/peste-porcina-africana', icon: 'virus', permiso: Permiso.PESTE_PORCINA_AFRICANA_VER },
      { label: 'Perfil Serológico Porcino', route: '/virologia/perfil-porcino', icon: 'grafico', permiso: Permiso.PERFIL_PORCINO_VER },
      { label: 'IBR, DVB y Leucosis (Bovinos)', route: '/virologia/perfil-bovino', icon: 'bovino', permiso: Permiso.PERFIL_BOVINO_VER },
      { label: 'Titulación de Vacuna (Newcastle)', route: '/virologia/titulacion-vacuna-newcastle', icon: 'vacuna', permiso: Permiso.TITULACION_VACUNA_VER },
      { label: 'Bronquitis Infecciosa Aviar', route: '/virologia/bronquitis', icon: 'pulmon', permiso: Permiso.BRONQUITIS_VER },
    ],
  },
  {
    // Las tres aviares por PCR en tiempo real. Comparten hoja y formato con su
    // pantalla de Virología, pero se trabajan aquí y con permiso aparte.
    label: 'Biología Molecular (BIO-MOL)',
    icon: 'dna',
    items: [
      { label: 'Influenza Aviar (PCR)', route: '/biologia-molecular/influenza-aviar', icon: 'dna', permiso: Permiso.INFLUENZA_AVIAR_PCR_VER },
      { label: 'Laringotraqueítis Aviar (PCR)', route: '/biologia-molecular/laringotraqueitis', icon: 'termociclador', permiso: Permiso.LARINGOTRAQUEITIS_PCR_VER },
      { label: 'Enfermedad de Newcastle (PCR)', route: '/biologia-molecular/newcastle', icon: 'molecula', permiso: Permiso.NEWCASTLE_PCR_VER },
    ],
  },
  {
    // La sección de más servicios del RT-52 (19 diagnósticos), de los que hoy
    // tienen formato el monitoreo ambiental —un formato para los tres servicios
    // que el RT-52 cobra aparte— y el bacteriológico de agua.
    label: 'Bacteriología/Micología (BAC/MIC)',
    icon: 'bacteria',
    // Se dibuja agrupada aunque a un usuario le quede visible una sola opción:
    // faltan muchas hojas y el grupo es lo que ubica la pantalla en su sección.
    siempreGrupo: true,
    items: [
      { label: 'Monitoreo Microbiológico de Ambiente', route: '/bacteriologia/monitoreo-ambiental', icon: 'ambiente', permiso: Permiso.MONITOREO_AMBIENTAL_VER },
      { label: 'Determinación cuantitativa de mesófilas y hongos', route: '/bacteriologia/recuento-mesofilas-hongos', icon: 'recuento', permiso: Permiso.RECUENTO_MESOFILAS_HONGOS_VER },
      { label: 'Bacteriológico de Agua', route: '/bacteriologia/bacteriologico-agua', icon: 'agua', permiso: Permiso.BACTERIOLOGICO_AGUA_VER },
      { label: 'Aislamiento e Identificación Bacteriana', route: '/bacteriologia/aislamiento-bacteriano', icon: 'cultivo', permiso: Permiso.AISLAMIENTO_BACTERIANO_VER },
      { label: 'Cultivo por Salmonella, E. coli y hongos', route: '/bacteriologia/cultivo-salmonella-hongos', icon: 'huevo', permiso: Permiso.CULTIVO_SALMONELLA_HONGOS_VER },
      { label: 'Aislamiento de Bacterias y Hongos', route: '/bacteriologia/aislamiento-bacterias-hongos', icon: 'hongo', permiso: Permiso.AISLAMIENTO_BACTERIAS_HONGOS_VER },
      { label: 'Aislamiento de M. bovis', route: '/bacteriologia/aislamiento-m-bovis', icon: 'microscopio', permiso: Permiso.AISLAMIENTO_M_BOVIS_VER },
      { label: 'Cultivo por Salmonella', route: '/bacteriologia/aislamiento-salmonella', icon: 'salmonella', permiso: Permiso.AISLAMIENTO_SALMONELLA_VER },
    ],
  },
  {
    // Sección propia en el RT-52 y en SIVESAH («Diagnóstico de Leptospira»),
    // aunque el agente sea una bacteria. La microaglutinación conserva la ruta
    // `leptospira-humano` de cuando convivía con la de animales, ya retirada.
    label: 'Leptospira (LEP)',
    icon: 'espiroqueta',
    siempreGrupo: true,
    items: [
      { label: 'Leptospira MAT', route: '/leptospirosis/leptospira-humano', icon: 'microaglutinacion', permiso: Permiso.LEPTOSPIRA_HUMANO_VER },
      { label: 'Aislamiento de Leptospira', route: '/leptospirosis/aislamiento-leptospira', icon: 'cultivo-lepto', permiso: Permiso.AISLAMIENTO_LEPTOSPIRA_VER },
      { label: 'Auto vacuna papiloma', route: '/leptospirosis/autovacuna-papiloma', icon: 'vacuna', permiso: Permiso.VACUNA_PAPILOMA_VER },
    ],
  },
  {
    // Un solo diagnóstico con dos etapas: la IFD sale en horas y la prueba
    // biológica se corre después. Cada una emite su propio informe, por eso
    // son dos opciones y no una.
    label: 'Rabia (RAB)',
    icon: 'rabia',
    siempreGrupo: true,
    items: [
      { label: 'Inmunofluorescencia Directa', route: '/rabia/ifd', icon: 'microscopio-fluor', permiso: Permiso.RABIA_VER },
      { label: 'Prueba Biológica', route: '/rabia/prueba-biologica', icon: 'raton', permiso: Permiso.RABIA_PRUEBA_BIOLOGICA_VER },
    ],
  },
  {
    // Sección propia en el IHIMV-RT-01, con un solo diagnóstico y un solo
    // ensayo (inmunocromatografía): por eso una única opción.
    label: 'Encefalopatía Espongiforme (EEB)',
    icon: 'cerebro',
    items: [
      { label: 'Diagnóstico de EEB', route: '/eeb/diagnostico', icon: 'cerebro', permiso: Permiso.EEB_VER },
    ],
  },
  {
    // La sección evalúa productos, no animales: su único servicio de hoy es la
    // evaluación biológica del plaguicida sobre teleoginas.
    label: 'Ixodicida (IXO)',
    icon: 'garrapata',
    items: [
      { label: 'Evaluación de ixodicida', route: '/ixodologia/evaluacion-ixodicida', icon: 'garrapata', permiso: Permiso.IXODICIDA_VER },
    ],
  },
  {
    // De los tres servicios de la sección solo el espermograma tiene formato
    // cargado; los otros dos entran por la bandeja genérica.
    label: 'Reproducción Animal (REPRO)',
    icon: 'espermatozoide',
    siempreGrupo: true,
    items: [
      { label: 'Espermograma', route: '/reproduccion/espermograma', icon: 'espermatozoide', permiso: Permiso.ESPERMOGRAMA_VER },
    ],
  },
  {
    // De los siete servicios de la sección tienen formato cargado Nosema, el
    // gusano barrenador y la identificación de escarabajo; los demás entran por
    // la bandeja genérica. La evaluación de ixodicida no va acá aunque su
    // formato diga «Parasitología»: su sección receptora es Ixodicida.
    label: 'Parasitología (PARA)',
    icon: 'abeja',
    items: [
      { label: 'Investigación de Nosema', route: '/parasitologia/nosema', icon: 'abeja', permiso: Permiso.NOSEMA_VER },
      { label: 'Gusano Barrenador (GBG)', route: '/parasitologia/gusano-barrenador', icon: 'larva', permiso: Permiso.GBG_VER },
      { label: 'Escarabajo (Aethina tumida)', route: '/parasitologia/aethina-tumida', icon: 'escarabajo', permiso: Permiso.AETHINA_TUMIDA_VER },
      // Ectoparásitos: una sola bandeja; el panel sale de la especie.
      { label: 'Ectoparásitos', route: '/parasitologia/ectoparasitos', icon: 'garrapata', permiso: Permiso.ECTOPARASITOS_VER },
      // Endoparásitos: una sola bandeja; el panel sale de la especie.
      { label: 'Endoparásitos', route: '/parasitologia/endoparasitos', icon: 'lombriz', permiso: Permiso.ENDOPARASITOS_VER },
    ],
  },
  {
    label: 'Resultados',
    icon: 'report',
    items: [
      { label: 'Revisión de resultados', route: '/resultados/revision', icon: 'report', permiso: Permiso.REVISION_VER },
      { label: 'Solicitudes de pago', route: '/excepciones-pago', icon: 'receipt', permiso: Permiso.EXCEPCIONES_VER },
    ],
  },
  {
    label: 'Administración',
    icon: 'collection',
    items: [
      { label: 'Tipos de catálogo', route: '/catalogo-tipos', icon: 'collection', permiso: Permiso.CATALOGO_TIPOS_VER },
      { label: 'Catálogos', route: '/catalogos', icon: 'list', permiso: Permiso.CATALOGOS_VER },
      { label: 'Servicios', route: '/servicios', icon: 'etiqueta', permiso: Permiso.SERVICIOS_VER },
      { label: 'Plantillas de documento', route: '/plantillas', icon: 'plantilla', permiso: Permiso.PLANTILLAS_VER },
      { label: 'Usuarios', route: '/usuarios', icon: 'usuarios', permiso: Permiso.USUARIOS_VER },
    ],
  },
];

/** Primera opción del menú que el usuario puede ver; null si no tiene ninguna. */
export function primeraRutaPermitida(can: (permiso?: string) => boolean): string | null {
  for (const seccion of SECCIONES) {
    for (const item of seccion.items) {
      if (can(item.permiso)) {
        return item.route;
      }
    }
  }
  return null;
}
